import React, { useMemo, useState } from 'react'
import {
    Box,
    CircularProgress,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core'
import { InputAdornment } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'

import { HeadCell } from '../../components'
import {
    VendorPerformanceAnalytics,
    VendorPerformanceProperty,
} from '../../hooks/useVendorPerformance'
import { formatCurrency, formatNumber, formatPercent } from './formatters'

type SortKey =
    | 'vendor_name'
    | 'property_name'
    | 'total_workorders'
    | 'cai_rate'
    | 'vendor_transitioned_wo_rate'
    | 'total_projects'
    | 'total_forecasted'
    | 'total_invoiced'
    | 'total_change_orders'
    | 'avg_change_orders_per_project'
    | 'early_workorder_rate'
    | 'go_back_rate'

const TABLE_HEAD_CELLS: HeadCell<{
    vendor_name: string
    property_name: string
    total_workorders: string
    cai_rate: string
    vendor_transitioned_wo_rate: string
    total_projects: string
    total_forecasted: string
    total_invoiced: string
    total_change_orders: string
    avg_change_orders_per_project: string
    early_workorder_rate: string
    go_back_rate: string
}>[] = [
    {
        id: 'vendor_name',
        label: 'Name',
        align: 'left',
        disablePadding: false,
        width: 200,
    },
    {
        id: 'property_name',
        label: 'Properties',
        align: 'left',
        disablePadding: false,
        width: 220,
    },
    {
        id: 'total_workorders',
        label: 'Work orders',
        align: 'right',
        disablePadding: false,
        width: 110,
    },
    {
        id: 'cai_rate',
        label: 'CAI',
        align: 'right',
        disablePadding: false,
        width: 90,
    },
    {
        id: 'vendor_transitioned_wo_rate',
        label: 'Vendor transitioned %',
        align: 'right',
        disablePadding: false,
        width: 160,
    },
    {
        id: 'total_projects',
        label: 'Projects',
        align: 'right',
        disablePadding: false,
        width: 90,
    },
    {
        id: 'total_forecasted',
        label: 'Forecasted',
        align: 'right',
        disablePadding: false,
        width: 120,
    },
    {
        id: 'total_invoiced',
        label: 'Invoiced',
        align: 'right',
        disablePadding: false,
        width: 120,
    },
    {
        id: 'total_change_orders',
        label: 'Change orders',
        align: 'right',
        disablePadding: false,
        width: 120,
    },
    {
        id: 'avg_change_orders_per_project',
        label: 'Avg COs / project',
        align: 'right',
        disablePadding: false,
        width: 140,
    },
    {
        id: 'early_workorder_rate',
        label: 'Early WO %',
        align: 'right',
        disablePadding: false,
        width: 110,
    },
    {
        id: 'go_back_rate',
        label: 'Go back %',
        align: 'right',
        disablePadding: false,
        width: 110,
    },
]

const useStyles = makeStyles((theme) => ({
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    searchField: {
        flex: 1,
        minWidth: 200,
    },
    inHouseToggle: {
        marginRight: 0,
        whiteSpace: 'nowrap',
    },
    tableWrapper: {
        flex: 1,
        display: 'flex',
        minHeight: 0,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 360px)',
        flexDirection: 'column',
    },
    table: {
        minWidth: 1570,
    },
    stickyHeader: {
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        whiteSpace: 'nowrap',
    },
    sortableHeader: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    propertyCell: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    propertyName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    propertyMore: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'default',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        minHeight: 52,
        maxHeight: 52,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 240,
    },
    error: {
        color: theme.palette.error.main,
        marginBottom: theme.spacing(2),
    },
}))

interface Props {
    rows: VendorPerformanceAnalytics[]
    isLoading: boolean
    isError?: boolean
}

const compareValues = (a: string | number, b: string | number) => {
    if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b, undefined, { sensitivity: 'base' })
    }
    return Number(a) - Number(b)
}

const firstPropertyName = (row: VendorPerformanceAnalytics) =>
    row.properties?.[0]?.name ?? ''

export const VendorPerformanceTable = ({ rows, isLoading, isError }: Props) => {
    const classes = useStyles()
    const [searchText, setSearchText] = useState('')
    const [includeInHouse, setIncludeInHouse] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [sortConfig, setSortConfig] = useState<{
        key: SortKey
        direction: 'asc' | 'desc'
    }>({
        key: 'cai_rate',
        direction: 'desc',
    })

    const filteredAndSortedRows = useMemo(() => {
        const searchLower = searchText.toLowerCase()
        const filtered = rows.filter((row) => {
            if (!includeInHouse && row.in_house) {
                return false
            }
            if (row.vendor_name.toLowerCase().includes(searchLower)) {
                return true
            }
            return (row.properties ?? []).some((property) =>
                property.name.toLowerCase().includes(searchLower),
            )
        })

        filtered.sort((a, b) => {
            const left =
                sortConfig.key === 'property_name'
                    ? firstPropertyName(a)
                    : a[sortConfig.key]
            const right =
                sortConfig.key === 'property_name'
                    ? firstPropertyName(b)
                    : b[sortConfig.key]
            const result = compareValues(left, right)
            return sortConfig.direction === 'asc' ? result : -result
        })
        return filtered
    }, [rows, searchText, sortConfig, includeInHouse])

    const paginatedRows = useMemo(() => {
        const startIndex = page * rowsPerPage
        return filteredAndSortedRows.slice(startIndex, startIndex + rowsPerPage)
    }, [filteredAndSortedRows, page, rowsPerPage])

    const handleSort = (key: SortKey) => {
        setSortConfig((previous) =>
            previous.key === key
                ? {
                      key,
                      direction: previous.direction === 'asc' ? 'desc' : 'asc',
                  }
                : {
                      key,
                      direction:
                          key === 'vendor_name' || key === 'property_name'
                              ? 'asc'
                              : 'desc',
                  },
        )
        setPage(0)
    }

    if (isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <>
            <div className={classes.searchContainer}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    placeholder="Search by vendor or property..."
                    value={searchText}
                    onChange={(event) => {
                        setSearchText(event.target.value)
                        setPage(0)
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControlLabel
                    className={classes.inHouseToggle}
                    control={
                        <Switch
                            color="primary"
                            checked={includeInHouse}
                            onChange={(event) => {
                                setIncludeInHouse(event.target.checked)
                                setPage(0)
                            }}
                        />
                    }
                    label="Include in-house"
                />
            </div>

            {isError && (
                <Typography className={classes.error}>
                    Unable to load vendor performance.
                </Typography>
            )}

            <TableContainer component={Paper} className={classes.tableWrapper}>
                <Table className={classes.table} size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {TABLE_HEAD_CELLS.map((cell) => (
                                <TableCell
                                    key={cell.id}
                                    align={cell.align}
                                    className={`${classes.stickyHeader} ${classes.sortableHeader}`}
                                    style={{ width: cell.width }}
                                    onClick={() =>
                                        handleSort(cell.id as SortKey)
                                    }
                                >
                                    {cell.label}
                                    {sortConfig.key === cell.id && (
                                        <span>
                                            {sortConfig.direction === 'asc'
                                                ? ' ↑'
                                                : ' ↓'}
                                        </span>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows.map((row) => (
                            <TableRow key={row.vendor_id} hover>
                                <TableCell>{row.vendor_name}</TableCell>
                                <TableCell>
                                    <PropertyOverflow
                                        properties={row.properties ?? []}
                                        classes={classes}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(row.total_workorders)}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip
                                        title={`${formatNumber(
                                            row.cai_count,
                                        )} of ${formatNumber(
                                            row.total_workorders,
                                        )} work orders`}
                                    >
                                        <span>
                                            {formatPercent(row.cai_rate)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip
                                        title={`${formatNumber(
                                            row.vendor_transitioned_count,
                                        )} of ${formatNumber(
                                            row.vendor_transitioned_total,
                                        )} transitions`}
                                    >
                                        <span>
                                            {formatPercent(
                                                row.vendor_transitioned_wo_rate,
                                            )}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(row.total_projects)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCurrency(row.total_forecasted)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatCurrency(row.total_invoiced)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(row.total_change_orders)}
                                </TableCell>
                                <TableCell align="right">
                                    {formatNumber(
                                        row.avg_change_orders_per_project,
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip
                                        title={`${formatNumber(
                                            row.early_workorder_count,
                                        )} of ${formatNumber(
                                            row.total_workorders,
                                        )} work orders with an early access request`}
                                    >
                                        <span>
                                            {formatPercent(
                                                row.early_workorder_rate,
                                            )}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip
                                        title={`${formatNumber(
                                            row.go_back_count,
                                        )} of ${formatNumber(
                                            row.total_workorders,
                                        )} work orders that had a go-back status`}
                                    >
                                        <span>
                                            {formatPercent(row.go_back_rate)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <div className={classes.footer}>
                <TablePagination
                    count={filteredAndSortedRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={(_event, newPage) => setPage(newPage)}
                    onChangeRowsPerPage={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10))
                        setPage(0)
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </div>
        </>
    )
}

const PropertyOverflow = ({
    properties,
    classes,
}: {
    properties: VendorPerformanceProperty[]
    classes: ReturnType<typeof useStyles>
}) => {
    if (properties.length === 0) {
        return null
    }

    const [first, ...rest] = properties
    return (
        <div className={classes.propertyCell}>
            <span className={classes.propertyName}>{first.name}</span>
            {rest.length > 0 && (
                <Tooltip
                    title={rest.map((property) => property.name).join(', ')}
                >
                    <span className={classes.propertyMore}>+{rest.length}</span>
                </Tooltip>
            )}
        </div>
    )
}
